// Router Configuration for TRADEOS.tech Dashboard

import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useRouteError,
  isRouteErrorResponse,
  useLocation,
} from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useProfileStore } from '../stores/profileStore';
import { useAuthStore, useAuthHydrated } from '../stores/authStore';
import { RoleGuard } from '../components/auth/RoleGuard';
import { PrivateAccessGuard } from '../components/auth/PrivateAccessGuard';
import FeedbackButton from '../components/FeedbackButton';
import { trackPageView } from '../lib/analytics';

const CHUNK_RELOAD_GUARD_KEY = 'tradeos_chunk_reload_guard';
const CHUNK_RELOAD_GUARD_WINDOW_MS = 30_000;

const extractErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }

  return '';
};

const isChunkLoadError = (error: unknown): boolean => {
  const message = extractErrorMessage(error).toLowerCase();
  if (!message) {
    return false;
  }

  return (
    message.includes('chunkloaderror') ||
    message.includes('loading chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('dynamically imported module')
  );
};

const hasRecentChunkReloadAttempt = (): boolean => {
  try {
    const lastAttemptRaw = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    if (!lastAttemptRaw) {
      return false;
    }
    const lastAttempt = Number(lastAttemptRaw);
    if (!Number.isFinite(lastAttempt)) {
      return false;
    }
    return Date.now() - lastAttempt < CHUNK_RELOAD_GUARD_WINDOW_MS;
  } catch {
    return true;
  }
};

const markChunkReloadAttempt = (): void => {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Ignore storage errors; reload guard is best-effort only.
  }
};

/**
 * Fires trackPageView on every navigation.
 * Must live inside RouterProvider so useLocation() works.
 */
const AnalyticsWatcher: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return (
    <>
      <Outlet />
      <FeedbackButton />
    </>
  );
};

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-400">Loading...</p>
    </div>
  </div>
);

// Loading screen for profile check
const ProfileCheckLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-400">Checking profile...</p>
    </div>
  </div>
);

// Error boundary component for handling route errors
const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const chunkLoadFailed = isChunkLoadError(error);
  const recentlyReloadedAfterChunkError = chunkLoadFailed ? hasRecentChunkReloadAttempt() : false;
  const [isAutoReloading, setIsAutoReloading] = useState(false);

  let title = 'Unexpected Error';
  let message = 'An unexpected error occurred. Please try again.';
  let statusCode = 500;

  useEffect(() => {
    if (!chunkLoadFailed || recentlyReloadedAfterChunkError) {
      return;
    }

    markChunkReloadAttempt();
    setIsAutoReloading(true);
    window.location.reload();
  }, [chunkLoadFailed, recentlyReloadedAfterChunkError]);

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    switch (error.status) {
      case 404:
        title = 'Page Not Found';
        message = "The page you're looking for doesn't exist or has been moved.";
        break;
      case 401:
        title = 'Unauthorized';
        message = 'You need to be logged in to access this page.';
        break;
      case 403:
        title = 'Forbidden';
        message = "You don't have permission to access this page.";
        break;
      default:
        title = `Error ${error.status}`;
        message = error.statusText || message;
    }
  } else if (chunkLoadFailed) {
    title = 'Dashboard Update Detected';
    statusCode = 503;
    if (recentlyReloadedAfterChunkError) {
      message =
        'This tab still has stale dashboard assets after an automatic refresh. Hard refresh this page (Ctrl+Shift+R / Cmd+Shift+R), then open Monitor again.';
    } else {
      message = isAutoReloading
        ? 'Refreshing once to load the latest dashboard assets...'
        : 'Refreshing once to load the latest dashboard assets.';
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="flex flex-col items-center gap-6 max-w-md text-center px-4">
        <div className="text-6xl">⚠️</div>
        <h1 className="text-2xl font-bold text-white">{title}</h1>
        <p className="text-gray-400">{message}</p>
        <div className="flex gap-4">
          <button
            onClick={() => (window.location.href = '/market')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors min-h-[44px]"
          >
            Go to Main
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-glass hover:bg-gray-600 text-white rounded-lg transition-colors min-h-[44px]"
          >
            Refresh Page
          </button>
        </div>
        {statusCode !== 404 && (
          <p className="text-xs text-gray-500 mt-4">Error code: {statusCode}</p>
        )}
      </div>
    </div>
  );
};

// 404 Not Found page
const NotFoundPage: React.FC = () => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="flex flex-col items-center gap-6 max-w-md text-center px-4">
        <div className="text-6xl">🔍</div>
        <h1 className="text-2xl font-bold text-white">Page Not Found</h1>
        <p className="text-gray-400">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => (window.location.href = '/market')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors min-h-[44px]"
          >
            Go to Main
          </button>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-glass hover:bg-gray-600 text-white rounded-lg transition-colors min-h-[44px]"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
};

// API error screen shown when the backend is unreachable (distinct from "no profile seeded")
const ApiErrorScreen: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-6 max-w-md text-center px-4">
      <div className="text-6xl">🔌</div>
      <h1 className="text-2xl font-bold text-white">Backend Unavailable</h1>
      <p className="text-gray-400">
        Could not reach the TRADEOS.tech API. Make sure all services are running.
      </p>
      {message && (
        <p className="text-xs text-gray-500 font-mono bg-glass-tertiary px-3 py-2 rounded">
          {message}
        </p>
      )}
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors min-h-[44px]"
      >
        Retry
      </button>
    </div>
  </div>
);

// Protected route component - requires active profile
const ProtectedRoute: React.FC = () => {
  const { activeProfile, isLoading, isLoaded, isApiError, error, setActiveProfile } =
    useProfileStore();
  const [pendingActivationFailed, setPendingActivationFailed] = useState(false);
  const pendingProfile = localStorage.getItem('tradeos_pending_profile');

  // Sync pending profile from localStorage on first load (handles post-signup flow)
  useEffect(() => {
    if (activeProfile) {
      setPendingActivationFailed(false);
      return;
    }

    if (isLoaded && pendingProfile) {
      setPendingActivationFailed(false);
      setActiveProfile(pendingProfile)
        .then(() => {
          localStorage.removeItem('tradeos_pending_profile');
        })
        .catch(() => {
          localStorage.removeItem('tradeos_pending_profile');
          setPendingActivationFailed(true);
        });
    }
  }, [isLoaded, activeProfile, pendingProfile, setActiveProfile]);

  // Show loading while the initial profile check is in progress.
  if (isLoading || !isLoaded) {
    return <ProfileCheckLoader />;
  }

  // API error (network down, 5xx) — show error screen
  if (isApiError) {
    return <ApiErrorScreen message={error ?? 'Could not connect to the API.'} />;
  }

  if (!activeProfile && pendingProfile && !pendingActivationFailed) {
    return <ProfileCheckLoader />;
  }

  // No active profile after pending activation flow → onboarding.
  if (!activeProfile) {
    return <Navigate to="/onboarding" replace />;
  }

  // Render protected private content after account/profile and view-pass checks.
  return (
    <PrivateAccessGuard>
      <Outlet />
    </PrivateAccessGuard>
  );
};

// Auth guard component - requires authentication
const AuthGuard: React.FC = () => {
  const { isAuthenticated, token } = useAuthStore();
  const hasHydrated = useAuthHydrated();

  // Wait for hydration to prevent flash of unauthenticated state
  // This prevents redirecting to sign-in before we've loaded the persisted auth state
  if (!hasHydrated) {
    return <PageLoader />;
  }

  // If not authenticated, redirect to sign in
  if (!isAuthenticated || !token) {
    return <Navigate to="/signin" replace />;
  }

  // Render protected content
  return <Outlet />;
};

// Lazy load pages
const AboutPage = lazy(() => import('../pages/landing/LandingPage'));
const HowItWorksPage = lazy(() => import('../pages/HowItWorks'));
const OnboardingPage = lazy(() => import('../pages/landing/OnboardingPage'));
const SignInPage = lazy(() => import('../pages/SignIn'));
const SignUpPage = lazy(() => import('../pages/SignUp'));
const DashboardPage = lazy(() => import('../pages/Dashboard'));
const TradingPage = lazy(() => import('../pages/Trading'));
const SymbolStoryPage = lazy(() => import('../pages/SymbolStory'));
const TokenDiscoveryPage = lazy(() => import('../pages/TokenDiscovery'));
const TokenBottomsPage = lazy(() => import('../pages/TokenBottoms'));
const PortfolioPage = lazy(() => import('../pages/Portfolio'));
const SignalsPage = lazy(() => import('../pages/Signals'));
const EAEntryQualityPage = lazy(() => import('../pages/EAEntryQuality'));
const RiskPage = lazy(() => import('../pages/Risk'));
const SettingsPage = lazy(() => import('../pages/Settings'));
const MonitorPage = lazy(() => import('../pages/Monitor'));
const ChatPage = lazy(() => import('../pages/Chat'));
const MetricsDashboard = lazy(() => import('../pages/MetricsDashboard'));
const LearningProgressPage = lazy(() => import('../pages/LearningProgress'));
const TuningDashboard = lazy(() => import('../pages/TuningDashboard'));
const ReplayAuditPage = lazy(() => import('../pages/ReplayAudit'));
const DecisionStoryPage = lazy(() => import('../pages/DecisionStory'));
const WowDashboard = lazy(() => import('../pages/WowDashboard'));
const StrategyPerformancePage = lazy(() => import('../pages/StrategyPerformance'));
const BacktestPage = lazy(() => import('../pages/Backtest'));
const TelegramTemplatesPage = lazy(() => import('../pages/TelegramTemplates'));
const FAQPage = lazy(() => import('../pages/FAQ'));
const TopologyPage = lazy(() => import('../pages/TopologyPage'));
const ReppoApprovalsPage = lazy(() => import('../pages/ReppoApprovals'));
const PublicIntelPublisherPage = lazy(() => import('../pages/PublicIntelPublisher'));
const TermsPage = lazy(() => import('../pages/TermsPage'));
const PrivacyPage = lazy(() => import('../pages/PrivacyPage'));
const SupportPage = lazy(() => import('../pages/Support'));
const ReportCardPage = lazy(() => import('../pages/ReportCard'));
const ForecastPage = lazy(() => import('../pages/Forecast'));
const ChartsPage = lazy(() => import('../pages/Charts'));
const MarketDirectionPage = lazy(() => import('../pages/MarketDirection'));
const MarketPulsePage = lazy(() => import('../pages/MarketPulse'));
const MarketPulseVpinPage = lazy(() => import('../pages/MarketPulseVpin'));
const PublicMarketDashboardPage = lazy(() => import('../pages/PublicMarketDashboard'));
const PublicMarketSignalsPage = lazy(() => import('../pages/PublicMarketSignals'));
const PlatformLoopsPage = lazy(() => import('../pages/PlatformLoops'));
const PublicVpinFlowStressPage = lazy(() => import('../pages/PublicVpinFlowStress'));
const PublicTokenDiscoveryPage = lazy(() => import('../pages/PublicTokenDiscovery'));
const PublicFusionSignalLitePage = lazy(() => import('../pages/PublicFusionSignalLite'));
const PublicWatchlistPage = lazy(() => import('../pages/PublicWatchlist'));
const TokenDossierPage = lazy(() => import('../pages/TokenDossier'));
const CreditsPage = lazy(() => import('../pages/Credits'));
const FeaturesPage = lazy(() => import('../pages/Features'));
const AskTradeOSPage = lazy(() => import('../pages/AskTradeOS'));
const FeedbackPage = lazy(() => import('../pages/Feedback'));
const ReviewLabPage = lazy(() => import('../pages/ReviewLab'));
const AdminLabelOpsPage = lazy(() => import('../pages/AdminLabelOps'));
const AdminIntelligenceFeedbackPage = lazy(() => import('../pages/AdminIntelligenceFeedback'));
const AdminFeedbackCommandCenterPage = lazy(() => import('../pages/AdminFeedbackCommandCenter'));
const AdminFeedbackOpsPage = lazy(() => import('../pages/AdminFeedbackOps'));
const AdminPublicIntelOpsPage = lazy(() => import('../pages/AdminPublicIntelOps'));
const VerifyEmailPage = lazy(() => import('../pages/VerifyEmail'));
const DeveloperKeysPage = lazy(() => import('../pages/DeveloperKeys'));
const BuilderFeedbackPortalPage = lazy(() => import('../pages/BuilderFeedbackPortal'));
const WatchlistsPage = lazy(() => import('../pages/Watchlists'));
const DossiersPage = lazy(() => import('../pages/Dossiers'));
const PrivateTokenDossierPage = lazy(() => import('../pages/PrivateTokenDossier'));
const PrivateAccessPage = lazy(() => import('../pages/PrivateAccess'));

// Create router — AnalyticsWatcher wraps the entire tree so every navigation fires trackPageView.
export const router = createBrowserRouter([
  {
    element: <AnalyticsWatcher />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Public routes - no auth required
      {
        path: '/',
        element: <Navigate to="/market" replace />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/market',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PublicMarketDashboardPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/market/signals',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PublicMarketSignalsPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/platform-loops',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformLoopsPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/vpin',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PublicVpinFlowStressPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/discover',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PublicTokenDiscoveryPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/fusion',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PublicFusionSignalLitePage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/watchlist',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PublicWatchlistPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/tokens/:tokenRef',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TokenDossierPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/credits',
        element: (
          <Suspense fallback={<PageLoader />}>
            <CreditsPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/features',
        element: (
          <Suspense fallback={<PageLoader />}>
            <FeaturesPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/private-access',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PrivateAccessPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/review',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReviewLabPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/admin/feedback',
        element: (
          <Suspense fallback={<PageLoader />}>
            <RoleGuard allowedRoles={['admin']}>
              <AdminFeedbackCommandCenterPage />
            </RoleGuard>
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/admin/label-ops',
        element: (
          <Suspense fallback={<PageLoader />}>
            <RoleGuard allowedRoles={['admin']}>
              <AdminLabelOpsPage />
            </RoleGuard>
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/admin/feedback-inbox',
        element: (
          <Suspense fallback={<PageLoader />}>
            <RoleGuard allowedRoles={['admin']}>
              <AdminIntelligenceFeedbackPage />
            </RoleGuard>
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/admin/feedback-ops',
        element: (
          <Suspense fallback={<PageLoader />}>
            <RoleGuard allowedRoles={['admin']}>
              <AdminFeedbackOpsPage />
            </RoleGuard>
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/admin/public-intel-ops',
        element: (
          <Suspense fallback={<PageLoader />}>
            <RoleGuard allowedRoles={['admin']}>
              <AdminPublicIntelOpsPage />
            </RoleGuard>
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/ask',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AskTradeOSPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/feedback',
        element: (
          <Suspense fallback={<PageLoader />}>
            <FeedbackPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/about',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AboutPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/how-it-works',
        element: (
          <Suspense fallback={<PageLoader />}>
            <HowItWorksPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/terms',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TermsPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/privacy',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PrivacyPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/signin',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SignInPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/signup',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SignUpPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/verify-email',
        element: (
          <Suspense fallback={<PageLoader />}>
            <VerifyEmailPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/support',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SupportPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/faq',
        element: (
          <Suspense fallback={<PageLoader />}>
            <FAQPage />
          </Suspense>
        ),
        errorElement: <RouteErrorBoundary />,
      },
      // Protected routes - require authentication
      {
        element: <AuthGuard />,
        errorElement: <RouteErrorBoundary />,
        children: [
          // Onboarding - requires auth but NOT an active profile
          {
            path: '/onboarding',
            element: (
              <Suspense fallback={<PageLoader />}>
                <OnboardingPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/developer/feedback',
            element: (
              <Suspense fallback={<PageLoader />}>
                <BuilderFeedbackPortalPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/build/feedback',
            element: <Navigate to="/developer/feedback" replace />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/developer',
            element: <Navigate to="/developer/feedback" replace />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/build',
            element: <Navigate to="/developer/feedback" replace />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/profile/feedback',
            element: <Navigate to="/credits" replace />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/account/feedback',
            element: <Navigate to="/credits" replace />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/developer/api-keys',
            element: (
              <Suspense fallback={<PageLoader />}>
                <DeveloperKeysPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/watchlists',
            element: (
              <Suspense fallback={<PageLoader />}>
                <WatchlistsPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          // Routes requiring both auth AND active profile
          {
            element: <ProtectedRoute />,
            errorElement: <RouteErrorBoundary />,
            children: [
              {
                path: '/dashboard',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DashboardPage />
                  </Suspense>
                ),
              },
              {
                path: '/trading',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <TradingPage />
                  </Suspense>
                ),
              },
              {
                path: '/trading/:symbol',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <TradingPage />
                  </Suspense>
                ),
              },
              {
                path: '/dossiers',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DossiersPage />
                  </Suspense>
                ),
              },
              {
                path: '/dossiers/:tokenRef',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateTokenDossierPage />
                  </Suspense>
                ),
              },
              {
                path: '/symbol-story',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SymbolStoryPage />
                  </Suspense>
                ),
              },
              {
                path: '/symbol-story/:symbol',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SymbolStoryPage />
                  </Suspense>
                ),
              },
              {
                path: '/token-discovery',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <TokenDiscoveryPage />
                  </Suspense>
                ),
              },
              {
                path: '/token-cycles',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <TokenBottomsPage />
                  </Suspense>
                ),
              },
              {
                path: '/token-bottoms',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <TokenBottomsPage />
                  </Suspense>
                ),
              },
              {
                path: '/symbols/:symbol/story',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SymbolStoryPage />
                  </Suspense>
                ),
              },
              {
                path: '/portfolio',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PortfolioPage />
                  </Suspense>
                ),
              },
              {
                path: '/signals',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SignalsPage />
                  </Suspense>
                ),
              },
              {
                path: '/ea-quality',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <EAEntryQualityPage />
                  </Suspense>
                ),
              },
              {
                path: '/risk',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <RiskPage />
                  </Suspense>
                ),
              },
              {
                path: '/settings',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SettingsPage />
                  </Suspense>
                ),
              },
              {
                path: '/settings/telegram',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <TelegramTemplatesPage />
                  </Suspense>
                ),
              },
              {
                path: '/monitor',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MonitorPage />
                  </Suspense>
                ),
              },
              {
                path: '/chat',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ChatPage />
                  </Suspense>
                ),
              },
              {
                path: '/metrics',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MetricsDashboard />
                  </Suspense>
                ),
              },
              {
                path: '/learning-progress',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <LearningProgressPage />
                  </Suspense>
                ),
              },
              {
                path: '/wow',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <WowDashboard />
                  </Suspense>
                ),
              },
              {
                path: '/tuning',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <RoleGuard allowedRoles={['approved', 'admin']}>
                      <TuningDashboard />
                    </RoleGuard>
                  </Suspense>
                ),
              },
              {
                path: '/replay-audit',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <RoleGuard allowedRoles={['approved', 'admin']}>
                      <ReplayAuditPage />
                    </RoleGuard>
                  </Suspense>
                ),
              },
              {
                path: '/reppo/approvals',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <RoleGuard allowedRoles={['approved', 'admin']}>
                      <ReppoApprovalsPage />
                    </RoleGuard>
                  </Suspense>
                ),
              },
              {
                path: '/public-intel',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <RoleGuard allowedRoles={['approved', 'admin']}>
                      <PublicIntelPublisherPage />
                    </RoleGuard>
                  </Suspense>
                ),
              },
              {
                path: '/decision-story',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DecisionStoryPage />
                  </Suspense>
                ),
              },
              {
                path: '/strategy',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <StrategyPerformancePage />
                  </Suspense>
                ),
              },
              {
                path: '/backtest',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <BacktestPage />
                  </Suspense>
                ),
              },
              {
                path: '/topology',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <RoleGuard allowedRoles={['approved', 'admin']}>
                      <TopologyPage />
                    </RoleGuard>
                  </Suspense>
                ),
              },
              {
                path: '/report-card',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ReportCardPage />
                  </Suspense>
                ),
              },
              {
                path: '/forecast',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ForecastPage />
                  </Suspense>
                ),
              },
              {
                path: '/charts',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ChartsPage />
                  </Suspense>
                ),
              },
              {
                path: '/market-direction',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MarketDirectionPage />
                  </Suspense>
                ),
              },
              {
                path: '/market-pulse',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MarketPulsePage />
                  </Suspense>
                ),
              },
              {
                path: '/market-pulse/vpin',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MarketPulseVpinPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      // Catch-all 404 route - must be last
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ], // end AnalyticsWatcher children
  },
]);

// Router provider component
export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default AppRouter;
